import moment from 'moment';
import {
  DATE_FORMAT,
  DEFAULT_MINIGAME_TYPES,
  MINI_GAME_DETAIL_METRICS,
  getMiniGameDimensionLabelKey,
  getMiniGameDimensionPlatform,
} from './config';

import WeixinIcon from '@intelligence/assets/imgs/casualDailyReports/icon-weixin.png';
import DouyinIcon from '@intelligence/assets/imgs/casualDailyReports/icon-dy.png';
import type { BasicInfoPlatform } from '@intelligence/v3/components/BasicInfoHeader/types';
import type { IStoreRankDetailsResData } from '@/pages/intelligence/types/storeRank';
import type { IGameDetailDateRange, IGameDetailBasicInfoParams } from './types';

const DIMENSION_KEYS = DEFAULT_MINIGAME_TYPES;

/**
 * 根据 gameId 前缀特征构建 BasicInfo 请求载荷
 */
export const buildBasicInfoPayload = (
  gameId: string,
): IGameDetailBasicInfoParams => {
  if (!gameId) return {};
  if (gameId.startsWith('wx') || gameId.includes('wechat')) {
    return { wechat_app_id: gameId };
  }
  if (
    gameId.startsWith('tt') ||
    gameId.startsWith('dy') ||
    gameId.includes('douyin')
  ) {
    return { douyin_app_id: gameId };
  }
  return { game_id: gameId };
};

export const isValidDate = (value?: string): boolean => {
  if (!value) return false;
  return moment(value, DATE_FORMAT, true).isValid();
};

/** 默认最近 7 天（与 "Last 7 Days" 快捷选项一致：[7天前, 昨天]） */
export const getDefaultDateRange = (initialRange: {
  startDate?: string;
  endDate?: string;
}): IGameDetailDateRange => {
  const endDate = isValidDate(initialRange?.endDate)
    ? (initialRange.endDate as string)
    : moment().subtract(1, 'day').format(DATE_FORMAT);
  const startDate = isValidDate(initialRange?.startDate)
    ? (initialRange.startDate as string)
    : moment(endDate).subtract(6, 'days').format(DATE_FORMAT);
  return { startDate, endDate };
};

export const getBasicInfoProducts = (basicInfo: Record<string, any> | null): any[] => {
  const candidates = [
    basicInfo?.same_company_products,
    basicInfo?.company_products,
    basicInfo?.same_company_games,
    basicInfo?.company_games,
    basicInfo?.related_products,
    basicInfo?.related_games,
    basicInfo?.product_list,
    basicInfo?.products,
    basicInfo?.game_list,
    basicInfo?.games,
  ];
  return candidates.find((value) => Array.isArray(value)) || [];
};

/** 获取维度对应的平台图标 */
export const getPlatformIcon = (dimension: string): string => {
  const platform = getMiniGameDimensionPlatform(dimension);
  if (platform === 'wechat') return WeixinIcon;
  if (platform === 'douyin') return DouyinIcon;
  return '';
};

/** 格式化维度显示名（去除平台前缀，由 icon 替代） */
export const formatMiniGameDimensionName = (
  dimension: string,
  formatMessage: (descriptor: { id: string }) => string,
): string => {
  const platform = getMiniGameDimensionPlatform(dimension);
  const formatted = formatMessage({ id: getMiniGameDimensionLabelKey(dimension) });
  if (platform === 'wechat') {
    return formatted.replace(/^WeChat\s+/i, '').replace(/^微信小游戏/, '');
  }
  if (platform === 'douyin') {
    return formatted.replace(/^Douyin\s+/i, '').replace(/^抖音小游戏/, '');
  }
  return formatted;
};

interface IMetricEntry {
  name: string;
  metric: any;
}

interface IMetricValue {
  rank: number | null;
  rank_dod: number | null;
}

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const result = Number(value);
  return Number.isFinite(result) ? result : null;
};

const normalizeDimension = (value: unknown): string => {
  const rawValue = String(value || '').toLowerCase();
  const dimension = DIMENSION_KEYS.find(item => rawValue.includes(item));
  return dimension || '';
};

const getMetricRank = (metric: any): number | null => {
  if (typeof metric === 'object' && metric !== null) {
    return toNumber(
      metric?.game_rank ?? metric?.rank ?? metric?.value ?? metric?.current,
    );
  }
  return toNumber(metric);
};

const getMetricDod = (metric: any): number | null => {
  if (!metric || typeof metric !== 'object') return null;
  return toNumber(
    metric?.game_rank_dod ??
      metric?.rank_dod ??
      metric?.rank_change ??
      metric?.growth_ratio ??
      metric?.dod,
  );
};

const getMetricEntries = (row: any): IMetricEntry[] => {
  const entries: IMetricEntry[] = [];
  const list = [
    row?.metric,
    row?.metrics,
    row?.metric_list,
    row?.source_metric_list,
  ].find(value => Array.isArray(value));

  if (Array.isArray(list)) {
    list.forEach((metric: any) => {
      const name =
        metric?.data_source ||
        metric?.source ||
        metric?.minigame_type ||
        metric?.name;
      const isRankMetric =
        !metric?.metric ||
        metric?.metric === 'game_rank' ||
        metric?.metric === 'rank';
      if (name || (row?.minigame_type && isRankMetric)) {
        entries.push({ name: name || row.minigame_type, metric });
      }
    });
  }

  if (row?.minigame_type) {
    entries.push({ name: row.minigame_type, metric: row });
  }

  DIMENSION_KEYS.forEach(dimension => {
    const field = Object.keys(row || {}).find(
      key => normalizeDimension(key) === dimension,
    );
    if (field) entries.push({ name: dimension, metric: row[field] });
  });

  return entries;
};

const getMetricsList = (response: any): any[] => {
  const data = response?.data ?? response;
  if (Array.isArray(data)) return data;
  return data?.list || data?.items || data?.records || data?.data?.list || [];
};

/**
 * 将 metrics 接口可能返回的 flat/nested 两类结构统一成详情核心组件需要的按日期数据。
 * 六个维度始终补齐，确保某个平台缺数据时仍保留对应的表头和折线维度。
 */
export const normalizeMiniGameMetrics = (
  response: any,
): IStoreRankDetailsResData[] => {
  const dateMap = new Map<string, Record<string, IMetricValue>>();

  getMetricsList(response).forEach((row: any) => {
    const date = row?.date || row?.day || row?.time;
    if (!date) return;
    const metricMap = dateMap.get(date) || {};

    getMetricEntries(row).forEach(({ name, metric }) => {
      const dimension = normalizeDimension(name);
      if (!dimension) return;
      metricMap[dimension] = {
        rank: getMetricRank(metric),
        rank_dod: getMetricDod(metric),
      };
    });

    dateMap.set(date, metricMap);
  });

  return Array.from(dateMap.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([date, metricMap]) => ({
      date,
      metric: DIMENSION_KEYS.map(name => ({
        name,
        rank: metricMap?.[name]?.rank ?? null,
        rank_dod: metricMap?.[name]?.rank_dod ?? null,
      })),
    })) as IStoreRankDetailsResData[];
};

const collectAppIds = (value: unknown, result: string[]): void => {
  if (typeof value === 'string' || typeof value === 'number') {
    if (String(value)) result.push(String(value));
    return;
  }
  if (Array.isArray(value)) {
    value.forEach(item => collectAppIds(item, result));
    return;
  }
  if (!value || typeof value !== 'object') return;
  const item = value as Record<string, any>;
  const appId =
    item?.app_id ||
    item?.appId ||
    item?.id ||
    item?.wechat_app_id ||
    item?.douyin_app_id;
  if (appId) result.push(String(appId));
};

export const normalizeMiniGameBasicInfo = (
  response: any,
): Record<string, any> | null => {
  const responseData = response?.data ?? response;
  const basicInfo = responseData?.data ?? responseData;
  if (!basicInfo || typeof basicInfo !== 'object') return null;

  // 映射接口字段为组件统一字段结构
  return {
    ...basicInfo,
    app_name: basicInfo?.game_name ?? basicInfo?.app_name,
    app_name_en: basicInfo?.game_name_en ?? basicInfo?.app_name_en,
    app_name_cn: basicInfo?.game_name_cn ?? basicInfo?.app_name_cn,
    name: basicInfo?.game_name ?? basicInfo?.app_name ?? basicInfo?.name,
    name_en: basicInfo?.game_name_en ?? basicInfo?.app_name_en ?? basicInfo?.name_en,
    name_cn: basicInfo?.game_name_cn ?? basicInfo?.app_name_cn ?? basicInfo?.name_cn,
    cover: basicInfo?.app_cover ?? basicInfo?.cover,
    icon: basicInfo?.app_cover ?? basicInfo?.icon,
    minigame_genre: basicInfo?.genre ?? basicInfo?.minigame_genre,
    minigame_genre_en: basicInfo?.genre_en ?? basicInfo?.minigame_genre_en,
    minigame_genre_cn: basicInfo?.genre_cn ?? basicInfo?.minigame_genre_cn,
    minigame_sub_genre: basicInfo?.sub_genre ?? basicInfo?.minigame_sub_genre,
    minigame_sub_genre_en: basicInfo?.sub_genre_en ?? basicInfo?.minigame_sub_genre_en,
    minigame_sub_genre_cn: basicInfo?.sub_genre_cn ?? basicInfo?.minigame_sub_genre_cn,
    developer: basicInfo?.publisher ?? basicInfo?.developer,
    developer_en: basicInfo?.publisher_en ?? basicInfo?.developer_en,
    developer_cn: basicInfo?.developer_cn ?? basicInfo?.publisher_cn,
    wechat_publisher: basicInfo?.wechat_publisher,
    wechat_publisher_en: basicInfo?.wechat_publisher_en,
    wechat_publisher_cn: basicInfo?.wechat_publisher_cn,
    douyin_publisher: basicInfo?.douyin_publisher,
    douyin_publisher_en: basicInfo?.douyin_publisher_en,
    douyin_publisher_cn: basicInfo?.douyin_publisher_cn,
    company_games: basicInfo?.company_games ?? basicInfo?.same_company_products ?? [],
  };
};

export const getMiniGameAppIds = (
  basicInfo: Record<string, any> | null,
): string[] => {
  if (!basicInfo) return [];
  const result: string[] = [];
  [
    basicInfo?.app_ids,
    basicInfo?.wechat_app_id,
    basicInfo?.douyin_app_id,
  ].forEach(value => collectAppIds(value, result));
  // 优先使用当前游戏 app id，避免把同公司产品弹层中的 id 一并提交给 metrics
  if (result.length > 0) return Array.from(new Set(result));
  [basicInfo?.app_id_list, basicInfo?.app_id_info].forEach(value =>
    collectAppIds(value, result),
  );
  return Array.from(new Set(result));
};

export const buildMiniGameMetricsParams = (payload: {
  appIds: string[];
  startDate: string;
  endDate: string;
  minigameGenre?: string[];
  minigameTypes?: string[];
}): Record<string, any> => ({
  entity_type: 'minigame',
  app_ids: payload.appIds,
  metrics: [...MINI_GAME_DETAIL_METRICS],
  minigame_types:
    payload.minigameTypes && payload.minigameTypes.length > 0
      ? payload.minigameTypes
      : DEFAULT_MINIGAME_TYPES,
  markets: ['global'],
  granularity: 'daily',
  group_by: ['game_id', 'date', 'minigame_type'],
  start_date: payload.startDate,
  end_date: payload.endDate,
  page: 1,
  page_size: 10000,
  filter: {
    minigame_genre: payload.minigameGenre || [],
  },
});

/**
 * 根据 Basic Info 中返回的平台覆盖情况（微信 / 抖音）解析 Header 平台图标
 */
export const getMiniGameHeaderPlatforms = (
  basicInfo: Record<string, any> | null,
  gameId?: string,
): BasicInfoPlatform[] => {
  if (!basicInfo) return [];

  const platforms: BasicInfoPlatform[] = [];
  const rawPlatforms = Array.isArray(basicInfo.platforms)
    ? basicInfo.platforms
    : [basicInfo.platforms || basicInfo.source];

  const hasWechat = Boolean(
    basicInfo.wechat_app_id ||
    rawPlatforms.includes('wechat') ||
    rawPlatforms.includes('weixin'),
  );

  const hasDouyin = Boolean(
    basicInfo.douyin_app_id ||
    rawPlatforms.includes('douyin'),
  );

  if (hasWechat) {
    platforms.push({ icon: WeixinIcon, alt: 'WeChat', key: 'wechat' });
  }
  if (hasDouyin) {
    platforms.push({ icon: DouyinIcon, alt: 'Douyin', key: 'douyin' });
  }

  // 兜底：未返回平台标识时根据 gameId 前缀推断
  if (platforms.length === 0 && gameId) {
    if (gameId.startsWith('tt') || gameId.startsWith('dy')) {
      platforms.push({ icon: DouyinIcon, alt: 'Douyin', key: 'douyin' });
    } else if (gameId.startsWith('wx')) {
      platforms.push({ icon: WeixinIcon, alt: 'WeChat', key: 'wechat' });
    }
  }

  return platforms;
};
